import { PropsWithChildren } from 'react';
import styled from '@emotion/styled';
import { SerializedStyles } from '@emotion/react';

export type OverflowType = 'hidden' | 'visible' | 'auto' | 'scroll';

export interface CardContainerProps extends PropsWithChildren {
  variant:
    | 'filled'
    | 'outline'
    | 'filledWithBorder'
    | 'elevated'
    | 'transparent'
    | 'error';
  disabled?: boolean;
  onClick?: () => void;
  emotionCss?: SerializedStyles;
  className?: string;
  role?: string;
  minHeight?: string;
  width?: string;
  overflow?: OverflowType;
}

export const CardContainer = ({
  variant,
  disabled,
  onClick,
  className,
  emotionCss,
  children,
  role,
  minHeight = '',
  width = '',
  overflow = 'hidden',
  ...otherProps
}: CardContainerProps) => {
  let Container;
  switch (variant) {
    case 'filled':
      Container = StyledFilledContainer;
      break;
    case 'outline':
      Container = StyledOutlineContainer;
      break;
    case 'filledWithBorder':
      Container = StyledFilledContainerWithBorder;
      break;
    case 'elevated':
      Container = StyledElevatedContainer;
      break;
    case 'transparent':
      Container = StyledTransparentContainer;
      break;
    default:
      Container = StyledFilledContainer;
  }

  return (
    <Container
      role={role}
      disabled={disabled}
      error={variant === 'error'}
      css={emotionCss}
      pointer={!!onClick}
      className={`rk-card${className ? ` ${className}` : ''}`}
      onClick={onClick}
      minHeight={minHeight}
      width={width}
      overflow={overflow}
      {...otherProps}
    >
      {children}
    </Container>
  );
};

interface StyledFilledContainerProps {
  disabled?: boolean;
  pointer?: boolean;
  minHeight?: string;
  width?: string;
  error?: boolean;
  overflow?: OverflowType;
}

const StyledFilledContainer = styled.div<StyledFilledContainerProps>`
  position: relative;
  background-color: ${(props) =>
    props.disabled
      ? 'var(--md-sys-color-surface-variant)'
      : props.error
        ? 'var(--md-sys-color-schemes-error-container)'
        : 'var(--md-sys-color-surface-container-highest)'};
  border-radius: var(--md-sys-shape-corner-lg);
  box-shadow: var(--md-sys-color-shadow);
  min-height: ${(props) => props.minHeight || '200px'};
  width: ${(props) => props.width || '100%'};
  opacity: ${(props) => (props.disabled ? 0.38 : 1)};
  overflow: ${(props) => props.overflow};
  ${(props) => props.pointer && { cursor: 'pointer' }}
`;
const StyledElevatedContainer = styled(StyledFilledContainer)`
  box-shadow: var(--rk-shadow-default);
  border: none;
  background-color: unset;
`;

const StyledFilledContainerWithBorder = styled(StyledFilledContainer)`
  border: 1px solid var(--md-sys-color-outline-variant);
`;

const StyledTransparentContainer = styled(StyledFilledContainer)`
  border: none;
  background-color: unset;
  background: unset;
  cursor: pointer;
`;

interface StyledOutlinedContainerProps {
  disabled?: boolean;
  pointer?: boolean;
  minHeight?: string;
  width?: string;
  overflow?: OverflowType;
}

const StyledOutlineContainer = styled.div<StyledOutlinedContainerProps>`
  position: relative;
  background-color: var(--md-sys-color-surface);
  border-radius: var(--md-sys-shape-corner-lg);
  box-shadow: var(--md-sys-color-shadow);
  min-height: ${(props) => props.minHeight || '200px'};
  width: ${(props) => props.width};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  overflow: ${(props) => props.overflow};
  ${(props) => props.pointer && { cursor: 'pointer' }}
  border: 1px solid var(
    ${(props) =>
    props.disabled
      ? '--md-sys-color-outline'
      : '--md-sys-color-outline-variant'}
  );
`;
